import React from "react";
import styled from "styled-components";
import { Bio } from "../../data/constants";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailIcon from "@mui/icons-material/Email";
import GitHubIcon from "@mui/icons-material/GitHub";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";

const FooterContainer = styled.div`
  width: 100%;
  padding: 3rem 0 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  background: ${({ theme }) => theme.bg || "transparent"};
  border-top: 1px solid ${({ theme }) => theme.text_primary + "15"};
`;

const FooterWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 1rem;
  color: ${({ theme }) => theme.text_primary};
`;

const Name = styled.div`
  font-weight: 700;
  font-size: 26px;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 0.25rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.secondary || "#764ba2"} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.3px;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Tagline = styled.div`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.5;
  margin-bottom: 1.5rem;
  max-width: 500px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin: 0.5rem 0 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -6px;
    width: 50px;
    height: 2px;
    background: ${({ theme }) => theme.primary};
    border-radius: 2px;
    transform: translateX(-50%);
  }
`;

const SocialMediaIcons = styled.div`
  display: flex;
  gap: 1rem;
  margin: 1rem 0 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const SocialMediaIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 10px;
  background: ${({ theme }) => theme.bgLight || "rgba(255, 255, 255, 0.05)"};
  color: ${({ theme }) => theme.text_primary};
  font-size: 1.3rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: 0.4s;
  }
  
  &:hover {
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.white || "#ffffff"};
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 6px 15px ${({ theme }) => theme.primary + "30"};
    
    &::before {
      left: 100%;
    }
  }
  
  &.linkedin:hover {
    background: #0A66C2;
  }
  
  &.github:hover {
    background: #333;
  }
  
  &.instagram:hover {
    background: linear-gradient(45deg, #405DE6, #5851DB, #833AB4, #C13584, #E1306C, #FD1D1D);
  }
  
  &.facebook:hover {
    background: #1877F2;
  }
  
  &.twitter:hover {
    background: #1DA1F2;
  }
  
  &.email:hover {
    background: #EA4335;
  }
`;

const Copyright = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.text_primary + "20"};
  width: 100%;
  max-width: 600px;
  text-align: center;
`;

const CopyrightText = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.5;
  margin: 0;
  
  & span {
    font-size: 0.85rem;
    opacity: 0.8;
    color: ${({ theme }) => theme.text_secondary};
    display: block;
    margin-top: 0.4rem;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  margin: 0.5rem 0 1rem;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.text_secondary};
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  justify-content: center;
  
  &:hover {
    color: ${({ theme }) => theme.primary};
    transform: translateY(-1px);
  }
  
  & svg {
    font-size: 1.2rem;
    opacity: 0.8;
  }
`;

const ConnectText = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.5;
  margin: 0.5rem 0 1rem;
  max-width: 500px;
  opacity: 0.9;
`;

const CompactFooter = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <FooterWrapper>
      
        
        <ContactInfo>
          {Bio.email && (
            <ContactItem href={`mailto:${Bio.email}`}>
              <EmailIcon />
              {Bio.email}
            </ContactItem>
          )}
          
          {Bio.phone && (
            <ContactItem href={`tel:${Bio.phone}`}>
              <PhoneIcon />
              {Bio.phone}
            </ContactItem>
          )}
        </ContactInfo>
        
        <SectionTitle>Connect With Me</SectionTitle>
        
        <ConnectText>
Let’s build something amazing together.
Connect with me on social media.        
</ConnectText>
        
        <SocialMediaIcons>
          {Bio.linkedin && (
            <SocialMediaIcon 
              href={Bio.linkedin} 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="linkedin"
            >
              <LinkedInIcon />
            </SocialMediaIcon>
          )}
          
          {Bio.insta && (
            <SocialMediaIcon 
              href={Bio.insta} 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="instagram"
            >
              <InstagramIcon />
            </SocialMediaIcon>
          )}
          
          {Bio.facebook && (
            <SocialMediaIcon 
              href={Bio.facebook} 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="facebook"
            >
              <FacebookIcon />
            </SocialMediaIcon>
          )}
        
        </SocialMediaIcons>
        
        <Copyright>
          <CopyrightText>
            &copy; {currentYear} {Bio.name || "Yathreb Samaali"}. All rights reserved.
            <span>Building the Future with Code</span>
          </CopyrightText>
        </Copyright>
      </FooterWrapper>
    </FooterContainer>
  );
};

export default CompactFooter;


